import React, { useCallback, useEffect, useState } from "react";
import {
  matchPath,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import PageSideBar from "components/PageSideBar";
import ProjectStatusBadge from "components/ProjectStatusBadge";
import ProjectService from "api/Project/ProjectService";
import ProjectData, { ProjectDataPlaceholder } from "./ProjectData";
import InfoTab from "./components/InfoTab";
import ApiKeysTab from "./components/ApiKeysTab";
import DeploymentTab from "./components/DeploymentTab";
import InitialisingState from "./components/InitialisingState";
import DashboardTab from "./components/DashboardTab";
import { AppRoutes, ProjectRoutes } from "pages/constants/routes";
import { ProjectStatus } from "models/ProjectStatus";

import styles from "./Project.module.scss";
import LoadingIcon from "components/LoadingIcon";

const initialisingTabs: { [key: string]: string } = {
  Initialising: ProjectRoutes.landing,
  Info: ProjectRoutes.info,
};

const tabs: { [key: string]: string } = {
  Dashboard: ProjectRoutes.landing,
  Deployment: ProjectRoutes.deployment,
  "API Keys": ProjectRoutes.apiKeys,
  Info: ProjectRoutes.info,
};

const getFullRoute = (projectRoute: string, proj_id: string | undefined) =>
  AppRoutes.project.replace(":proj_id", proj_id ?? "") + projectRoute;

const Project = () => {
  const { proj_id } = useParams();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [projectData, setProjectData] = useState<ProjectData>(
    ProjectDataPlaceholder,
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const getProjectData = useCallback(async () => {
    if (proj_id === undefined) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await ProjectService.getProject(proj_id);
      setProjectData(response.project);
    } catch (err) {
      // TODO: Catch error
    } finally {
      setIsLoading(false);
    }
  }, [proj_id]);

  useEffect(() => {
    getProjectData();
  }, [getProjectData]);

  const tabsToDisplay =
    projectData.project_status === ProjectStatus.ready
      ? tabs
      : initialisingTabs;

  const renderInitialisingState = () => <InitialisingState />;

  const renderLoadingState = () => (
    <div className={styles.content}>
      <div className={styles.loadingBox}>
        <LoadingIcon />
        <div>Loading Project</div>
      </div>
    </div>
  );

  const renderProject = () => (
    <div className={styles.content}>
      <div className={styles.leftTabGroup}>
        <div className={styles.header}>
          <span>{projectData?.name}</span>
          <ProjectStatusBadge status={projectData?.project_status} />
        </div>
        <div className={styles.tabs}>
          {Object.keys(tabsToDisplay).map((tab) => (
            <div
              key={tab}
              className={`${styles.tab} ${matchPath(getFullRoute(tabsToDisplay[tab], proj_id), pathname) && styles.selected}`}
              onClick={() => {
                navigate(getFullRoute(tabsToDisplay[tab], proj_id));
              }}
            >
              {tab}
            </div>
          ))}
        </div>
      </div>
      <div className={styles.tabContent}>
        <Routes>
          <Route
            index
            path={ProjectRoutes.landing}
            element={<DashboardTab proj_id={proj_id ?? ""} />}
          />
          <Route
            path={ProjectRoutes.deployment}
            element={<DeploymentTab proj_id={proj_id ?? ""} />}
          />
          <Route
            path={ProjectRoutes.apiKeys}
            element={<ApiKeysTab proj_id={proj_id ?? ""} />}
          />
          <Route
            path={ProjectRoutes.info}
            element={<InfoTab proj_id={proj_id ?? ""} />}
          />
        </Routes>
        <Outlet />
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      <PageSideBar />
      {isLoading
        ? renderLoadingState()
        : projectData.project_status === ProjectStatus.intiailising
          ? renderInitialisingState()
          : renderProject()}
    </div>
  );
};

export default Project;
